import React from 'react';
import { BsX, BsSearch, BsCalendar3, BsShuffle, BsXCircle } from 'react-icons/bs';

interface HelpDrawerEmailProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string; textColor?: string }> = ({ icon, label, description, bgColor, borderColor, textColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
      color: textColor || 'black',
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerEmail: React.FC<HelpDrawerEmailProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Email Candidatos</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: 0 }}>
              Envie emails de convocação de heteroidentificação para os candidatos do processo. Selecione individualmente, aleatoriamente ou envie para todos.
            </p>
          </Section>

          <Section title="Data da Banca">
            <ActionItem icon={<BsCalendar3 />} label="Selecionar Data" description="Opcionalmente, vincule a convocação a uma data de banca específica. O email incluirá a data e horário." bgColor="#f8f9fa" />
          </Section>

          <Section title="Seleção de Candidatos">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Checkbox individual:</strong> Seleciona/deseleciona um candidato.</li>
              <li><strong>Checkbox do header:</strong> Seleciona/deseleciona todos da página atual.</li>
              <li><strong>Busca:</strong> Filtra por nome ou email.</li>
              <li>A seleção é mantida ao navegar entre páginas.</li>
            </ul>
          </Section>

          <Section title="Ações">
            <ActionItem icon={<BsShuffle />} label="Selecionar Aleatoriamente" description="Seleciona N candidatos aleatórios do total. Informe a quantidade no campo numérico." bgColor="#007bff" borderColor="#007bff" textColor="white" />
            <ActionItem icon={<BsXCircle />} label="Limpar Seleção" description="Remove todos os candidatos da seleção atual." bgColor="#dc3545" borderColor="#dc3545" textColor="white" />
            <ActionItem icon={<span style={{ fontSize: '10px' }}>✉</span>} label="Enviar para Todos" description="Envia convocação para todos os candidatos do processo (ignora seleção)." bgColor="#ffc107" borderColor="#ffc107" />
            <ActionItem icon={<span style={{ fontSize: '10px' }}>✉</span>} label="Enviar para Selecionados" description="Envia convocação apenas para os candidatos selecionados." bgColor="#28a745" borderColor="#28a745" textColor="white" />
          </Section>

          <Section title="Status do Email">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: 'red' }}>✗</span>
                <span><strong>Não enviado:</strong> Email ainda não foi enviado.</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: 'orange' }}>⚠</span>
                <span><strong>Aguardando:</strong> Enviado, aguardando confirmação do candidato.</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: 'green' }}>✓</span>
                <span><strong>Confirmado:</strong> Candidato confirmou recebimento.</span>
              </div>
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Candidatos com status VALIDADO são ignorados no envio.</li>
              <li>Candidatos sem email são ignorados automaticamente.</li>
              <li>Processos finalizados ou cancelados não permitem envio.</li>
              <li>Um modal de confirmação é exibido antes do envio.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerEmail;
